import React from "react";
import { motion } from "framer-motion";
import { 
  Wine, 
  Home, 
  Map, 
  CheckCircle2, 
  ArrowRight, 
  Palmtree, 
  MapPin, 
  ShieldCheck, 
  Grape,
  Building2,
  LayoutGrid
} from "lucide-react";
import { Link } from "react-router-dom";
import { IMAGES } from "@/assets/images";
import { ROUTE_PATHS, Service } from "@/lib/index";
import { SERVICES_DATA } from "@/data/index";
import { FeatureCard } from "@/components/Cards";
import { springPresets, fadeInUp, staggerContainer, staggerItem } from "@/lib/motion";

const CATEGORY_ICONS: Record<string, React.ReactNode> = {
  wine: <Wine className="w-6 h-6 text-primary" />,
  residence: <Home className="w-6 h-6 text-primary" />,
  land: <Map className="w-6 h-6 text-primary" />,
};

const FEATURE_ICONS: Record<string, React.ReactNode[]> = {
  wine: [
    <Grape className="w-5 h-5" />,
    <Wine className="w-5 h-5" />,
    <ArrowRight className="w-5 h-5" />,
    <CheckCircle2 className="w-5 h-5" />
  ],
  residence: [
    <MapPin className="w-5 h-5" />,
    <Building2 className="w-5 h-5" />,
    <Home className="w-5 h-5" />,
    <Palmtree className="w-5 h-5" />
  ],
  land: [
    <ShieldCheck className="w-5 h-5" />,
    <FileTextIcon className="w-5 h-5" />,
    <LayoutGrid className="w-5 h-5" />,
    <ArrowRight className="w-5 h-5" />
  ]
};

function FileTextIcon({ className }: { className?: string }) {
  return (
    <svg 
      xmlns="http://www.w3.org/2000/svg" 
      width="24" 
      height="24" 
      viewBox="0 0 24 24" 
      fill="none" 
      stroke="currentColor" 
      strokeWidth="2" 
      strokeLinecap="round" 
      strokeLinejoin="round" 
      className={className}
    >
      <path d="M15 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7Z" />
      <path d="M14 2v4a2 2 0 0 0 2 2h4" />
      <path d="M10 9H8" />
      <path d="M16 13H8" />
      <path d="M16 17H8" />
    </svg>
  );
}

const Services: React.FC = () => {
  return (
    <div className="flex flex-col w-full overflow-hidden">
      {/* Hero Section */}
      <section className="relative pt-32 pb-20 md:pt-48 md:pb-32 bg-background">
        <div className="container mx-auto px-4 relative z-10">
          <motion.div 
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="max-w-3xl"
          >
            <h1 className="text-4xl md:text-6xl font-heading mb-6">
              Nos Services de <span className="text-primary">Prestige</span>
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground leading-relaxed">
              Chez YKE Multi_Services, nous fusionnons l'élégance des grands crus, le confort des résidences d'exception et la sécurité des investissements fonciers pour répondre à vos exigences les plus élevées.
            </p>
          </motion.div>
        </div>
        
        <div className="absolute top-0 right-0 w-1/3 h-full bg-gradient-to-l from-primary/5 to-transparent hidden lg:block" />
      </section>

      {/* Detailed Services Sections */}
      <section className="py-20 bg-background">
        <div className="container mx-auto px-4 space-y-32">
          {SERVICES_DATA.map((service, index) => (
            <div 
              key={service.id} 
              id={service.id}
              className={`flex flex-col lg:flex-row items-center gap-12 lg:gap-24 ${index % 2 !== 0 ? 'lg:flex-row-reverse' : ''}`}
            >
              {/* Image Side */}
              <motion.div 
                initial={{ opacity: 0, x: index % 2 === 0 ? -50 : 50 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={springPresets.gentle}
                className="w-full lg:w-1/2"
              >
                <div className="relative group">
                  <div className="absolute -inset-4 bg-primary/10 rounded-2xl blur-xl group-hover:bg-primary/20 transition-all duration-500" />
                  <div className="relative aspect-[4/3] rounded-2xl overflow-hidden border border-border">
                    <img 
                      src={IMAGES[service.imageKey]}
                      alt={service.title}
                      className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-background/60 to-transparent" />
                    <div className="absolute bottom-6 left-6">
                      <div className="bg-background/80 backdrop-blur-md p-3 rounded-full border border-white/10">
                        {CATEGORY_ICONS[service.category]}
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>

              {/* Content Side */}
              <div className="w-full lg:w-1/2 space-y-8">
                <motion.div 
                  initial="hidden"
                  whileInView="visible"
                  viewport={{ once: true }}
                  variants={staggerContainer}
                  className="space-y-6"
                >
                  <motion.div variants={staggerItem}>
                    <span className="text-primary font-mono text-sm tracking-widest uppercase">
                      Secteur d'activité 0{index + 1}
                    </span>
                    <h2 className="text-3xl md:text-4xl font-heading mt-2">{service.title}</h2>
                  </motion.div>

                  <motion.p 
                    variants={staggerItem}
                    className="text-muted-foreground text-lg leading-relaxed"
                  >
                    {service.fullDescription}
                  </motion.p>

                  {service.locations && (
                    <motion.div variants={staggerItem} className="flex flex-wrap gap-3">
                      {service.locations.map(loc => (
                        <span key={loc} className="inline-flex items-center px-4 py-1.5 rounded-full bg-accent text-accent-foreground text-sm font-medium border border-border">
                          <MapPin className="w-3.5 h-3.5 mr-2" />
                          {loc}
                        </span>
                      ))}
                    </motion.div>
                  )}

                  <motion.div 
                    variants={staggerItem}
                    className="grid grid-cols-1 md:grid-cols-2 gap-4 pt-4"
                  >
                    {service.features.map((feature, fIndex) => (
                      <FeatureCard 
                        key={fIndex}
                        title={feature}
                        description=""
                        icon={FEATURE_ICONS[service.category][fIndex] || <CheckCircle2 className="w-5 h-5" />}
                      />
                    ))}
                  </motion.div>

                  <motion.div variants={staggerItem} className="pt-6">
                    <Link 
                      to={ROUTE_PATHS.CONTACT} 
                      className="inline-flex items-center justify-center px-8 py-4 rounded-full bg-primary text-primary-foreground font-semibold hover:opacity-90 transition-all group"
                    >
                      Demander un devis
                      <ArrowRight className="ml-2 w-5 h-5 group-hover:translate-x-1 transition-transform" />
                    </Link>
                  </motion.div>
                </motion.div>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* Process Section */}
      <section className="py-24 bg-muted/30">
        <div className="container mx-auto px-4">
          <div className="text-center max-w-2xl mx-auto mb-16">
            <h2 className="text-3xl font-heading mb-4">Pourquoi nous choisir ?</h2>
            <p className="text-muted-foreground">Une expertise reconnue et un accompagnement personnalisé pour tous vos projets.</p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[ 
              { 
                title: "Transparence Totale", 
                desc: "Chaque transaction, que ce soit pour un vin rare ou un terrain, est documentée et sécurisée juridiquement.",
                icon: <ShieldCheck className="w-10 h-10 text-primary" />
              },
              { 
                title: "Qualité Certifiée", 
                desc: "Nous sélectionnons uniquement le meilleur, des résidences tout confort aux champagnes de grandes maisons.",
                icon: <CheckCircle2 className="w-10 h-10 text-primary" />
              },
              { 
                title: "Proximité Client", 
                desc: "Une équipe dédiée basée à Port-Bouët pour répondre à vos besoins avec réactivité et professionnalisme.",
                icon: <ArrowRight className="w-10 h-10 text-primary" />
              }
            ].map((item, i) => (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: i * 0.1 }}
                className="bg-card p-8 rounded-2xl border border-border hover:shadow-xl transition-all duration-300"
              >
                <div className="mb-6">{item.icon}</div>
                <h3 className="text-xl font-semibold mb-3">{item.title}</h3>
                <p className="text-muted-foreground">{item.desc}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Final CTA */}
      <section className="py-24 bg-background relative overflow-hidden">
        <div className="absolute inset-0 bg-primary/5 opacity-50" />
        <div className="container mx-auto px-4 relative z-10 text-center">
          <motion.div
            initial="hidden"
            whileInView="visible"
            viewport={{ once: true }}
            variants={fadeInUp}
            className="max-w-3xl mx-auto"
          >
            <h2 className="text-4xl md:text-5xl font-heading mb-8">
              Prêt à concrétiser votre <span className="text-primary">Projet</span> ?
            </h2>
            <p className="text-xl text-muted-foreground mb-12">
              Qu'il s'agisse d'une commande de champagne, d'une réservation de résidence ou d'un investissement foncier, nos experts sont à votre écoute.
            </p>
            <div className="flex flex-col sm:flex-row gap-6 justify-center">
              <Link 
                to={ROUTE_PATHS.CONTACT}
                className="px-10 py-5 bg-primary text-primary-foreground rounded-full font-bold text-lg hover:scale-105 active:scale-95 transition-all shadow-lg shadow-primary/20"
              >
                Nous contacter
              </Link>
              <Link 
                to={ROUTE_PATHS.ABOUT}
                className="px-10 py-5 bg-secondary text-secondary-foreground border border-border rounded-full font-bold text-lg hover:bg-accent transition-all"
              >
                En savoir plus sur nous
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default Services;